/* Branded bin labels from the selected public product reference; no price/account data. */
(function (root, factory) {
  const core = factory();
  if (typeof module === "object" && module.exports) module.exports = core;
  if (!root?.document || root.customElements.get("saber-bin-labels")) return;
  const doc = root.document;
  const node = (tag, text, attrs = {}) => {
    const el = doc.createElement(tag);
    if (text !== undefined) el.textContent = text;
    Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, String(value)));
    return el;
  };
  const button = (text, action, className = "") => {
    const el = node("button", text, { type: "button", class: className });
    el.addEventListener("click", action);
    return el;
  };
  const field = (text, input) => {
    const el = node("label", undefined, { class: "ss-bin-label-field" });
    input.setAttribute("aria-label", text);
    el.append(node("span", text), input);
    return el;
  };
  const imageCache = new Map();
  async function rasterData(value, signal) {
    const url = new URL(value, root.location.href);
    if (url.username || url.password || (url.protocol !== "https:" && url.origin !== root.location.origin))
      throw new Error("Image address is not supported.");
    if (imageCache.has(url.href)) return imageCache.get(url.href);
    const response = await fetch(url.href, { credentials: "omit", mode: "cors", redirect: "error", signal });
    const mime = (response.headers.get("content-type") || "").split(";")[0].toLowerCase();
    if (!response.ok || !["image/png", "image/jpeg", "image/webp", "image/gif", "image/avif"].includes(mime))
      throw new Error("A supported product image could not be loaded.");
    const cap = 8 * 1024 * 1024;
    if (Number(response.headers.get("content-length")) > cap) throw new Error("Image is too large.");
    const reader = response.body?.getReader();
    const chunks = [];
    let size = 0;
    if (reader) {
      try {
        for (;;) {
          const result = await reader.read();
          if (result.done) break;
          size += result.value.byteLength;
          if (size > cap) throw new Error("Image is too large.");
          chunks.push(result.value);
        }
      } catch (error) {
        await reader.cancel().catch(() => {});
        throw error;
      }
    } else {
      const bytes = await response.arrayBuffer();
      if (bytes.byteLength > cap) throw new Error("Image is too large.");
      chunks.push(bytes);
    }
    const bitmap = await root.createImageBitmap(new Blob(chunks, { type: mime }));
    if (!bitmap.width || !bitmap.height || bitmap.width * bitmap.height > 40000000) {
      bitmap.close();
      throw new Error("Image dimensions are not supported.");
    }
    const scale = Math.min(1, 640 / bitmap.width, 400 / bitmap.height);
    const canvas = doc.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const data = canvas.toDataURL("image/png");
    if (imageCache.size >= 24) imageCache.delete(imageCache.keys().next().value);
    imageCache.set(url.href, data);
    return data;
  }
  function saveSVG(svg, filename) {
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
    const a = node("a", undefined, { href: url, download: filename });
    doc.body.append(a);
    a.click();
    a.remove();
    root.setTimeout(() => URL.revokeObjectURL(url), 2000);
  }
  async function printLabels(svg, options, opener, report, stillValid, signal) {
    let html;
    try { html = core.printDocument(svg, options); }
    catch { report.textContent = "Choose a valid label size and between 1 and 50 whole copies."; return; }
    const frame = node("iframe", undefined, { title: "Bin label print document" });
    frame.style.cssText = "position:fixed;width:1px;height:1px;left:-10000px;top:0;border:0";
    doc.body.append(frame);
    const target = frame.contentDocument;
    target.open();
    target.write(html);
    target.close();
    let timer;
    let preparationTimer;
    let abortPreparation;
    const onAbort = () => { abortPreparation?.(); cleanup(); };
    const cleanup = () => {
      root.clearTimeout(timer);
      root.clearTimeout(preparationTimer);
      signal?.removeEventListener("abort", onAbort);
      frame.remove();
      if (opener.isConnected && !opener.disabled) opener.focus();
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    frame.contentWindow.addEventListener("afterprint", cleanup, { once: true });
    report.textContent = "Preparing the print document…";
    try {
      const images = Array.from(target.querySelectorAll("image"), async (element) => {
        const image = new frame.contentWindow.Image();
        image.src = element.getAttribute("href");
        await image.decode();
      });
      await Promise.race([
        Promise.all([...images, target.fonts?.ready || Promise.resolve()]),
        new Promise((_, reject) => { abortPreparation = () => reject(new Error("Print cancelled.")); if (signal?.aborted) abortPreparation(); }),
        new Promise((_, reject) => { preparationTimer = root.setTimeout(() => reject(new Error("Print preparation timed out.")), 8000); }),
      ]);
      abortPreparation = null;
      root.clearTimeout(preparationTimer);
      if (signal?.aborted || !stillValid() || !frame.isConnected) { cleanup(); return; }
      // Some browsers omit afterprint. Keep the fully decoded document long enough for the print dialog.
      timer = root.setTimeout(cleanup, 120000);
      frame.contentWindow.focus();
      frame.contentWindow.print();
      report.textContent = "Print opened. Choose actual size / 100%; you can also save as PDF in your browser.";
    } catch {
      cleanup();
      if (!signal?.aborted) report.textContent = "The print document could not be prepared. Download the SVG and print it at actual size.";
    }
  }
  class BinLabels extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.openButton = this.querySelector(".ss-bin-labels-open");
      this.status = this.querySelector(".ss-bin-labels-status");
      try {
        this.data = core.normalizeData(JSON.parse(this.querySelector("[data-bin-label-data]").dataset.binLabelData));
      } catch {
        this.status.textContent = "This product's label information is unavailable. Please contact us with the product name.";
        return;
      }
      this.updateState = () => {
        const valid = this.selectionValid();
        this.openButton.disabled = !valid;
        this.status.textContent = valid ? "" : "Choose product options to create your label.";
        if (!valid) this.closeDialog();
      };
      this.openButton.addEventListener("click", () => this.openDialog());
      this.scope = this.closest(".ss-pdp") || this.parentElement;
      this.observer = new MutationObserver(this.updateState);
      this.observer.observe(this.scope, { attributes: true, attributeFilter: ["data-variant-loading", "data-selection-unavailable"] });
      this.formChange = () => this.updateState();
      doc.addEventListener("change", this.formChange);
      this.updateState();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      doc.removeEventListener("change", this.formChange);
      this.closeDialog();
    }
    selectionValid() {
      const product = this.closest(".ss-pdp");
      if (!this.data?.selected || !this.data.variantId || product?.dataset.variantLoading === "true" || product?.dataset.selectionUnavailable === "true") return false;
      const form = doc.getElementById(this.dataset.formId);
      const id = form?.elements.namedItem("id")?.value;
      // Out-of-stock products can still be labeled. Disabled commerce controls do not invalidate their identity.
      return !id || String(id) === this.data.variantId;
    }
    closeDialog() {
      if (!this.dialog) return;
      this.abort?.abort();
      this.dialog.close();
      this.dialog.remove();
      this.dialog = null;
      if (this.openButton?.isConnected && !this.openButton.disabled) this.openButton.focus();
    }
    async openDialog() {
      if (!this.selectionValid()) return this.updateState();
      this.closeDialog();
      const id = "SaberBinLabel-" + String(this.dataset.instance || "product").replace(/[^A-Za-z0-9_-]/g, "-");
      const dialog = node("dialog", undefined, { class: "ss-bin-label-dialog", "aria-labelledby": id });
      this.dialog = dialog;
      dialog.addEventListener("cancel", (event) => { event.preventDefault(); this.closeDialog(); });
      const heading = node("div", undefined, { class: "ss-bin-label-heading" });
      const headingText = node("div");
      headingText.append(node("p", "Product identification", { class: "ss-bin-label-eyebrow" }), node("h2", "Create your bin label", { id }));
      heading.append(headingText, button("×", () => this.closeDialog(), "ss-bin-label-close"));
      heading.lastChild.setAttribute("aria-label", "Close bin label preview");
      const content = node("div", undefined, { class: "ss-bin-label-content" });
      const previewPanel = node("div", undefined, { class: "ss-bin-label-preview-panel" });
      const preview = node("div", undefined, { class: "ss-bin-label-preview", "aria-label": "Bin label preview" });
      const caption = node("p", "Preview scales to your screen. Printed dimensions stay true to the selected size.", { class: "ss-bin-label-preview-caption" });
      previewPanel.append(preview, caption);
      const controls = node("div", undefined, { class: "ss-bin-label-options" });
      const loadStatus = node("p", "Preparing your logo and product image…", { class: "ss-bin-label-load-status", role: "status" });
      const size = node("select");
      size.append(node("option", "4 × 2 in · 101.6 × 50.8 mm", { value: "small" }), node("option", "6 × 3 in · 152.4 × 76.2 mm", { value: "large" }));
      const paper = node("select");
      paper.append(node("option", "Individual label / roll", { value: "label" }), node("option", "US Letter sheet", { value: "letter" }), node("option", "A4 sheet", { value: "a4" }));
      const count = node("input", undefined, { type: "number", min: "1", max: "50", step: "1", value: "1", inputmode: "numeric", required: "" });
      const warning = node("p", undefined, { class: "ss-bin-label-note ss-bin-label-warning", role: "status", hidden: "" });
      const barcodeInfo = node("p", undefined, { class: "ss-bin-label-footnote" });
      const note = node("p", "Print at actual size / 100%. Turn off browser headers and footers. Match your paper or label stock to the dimensions above.", { class: "ss-bin-label-note" });
      const actions = node("div", undefined, { class: "ss-bin-label-actions" });
      let label;
      let loading = true;
      const opts = () => ({ ...(size.value === "large" ? core.SIZES.large : core.SIZES.small), paper: paper.value, count: Number(count.value) });
      const download = button("Download SVG label", () => {
        if (this.selectionValid() && label?.svg && !label.error) saveSVG(label.svg, core.filename(this.data));
      }, "ss-bin-label-primary");
      const print = button("Print labels / save PDF", async () => {
        if (!this.selectionValid() || !label?.svg || label.error) return;
        if (!count.checkValidity()) return count.reportValidity();
        if (this.printing) return;
        this.printing = true;
        print.disabled = true;
        try { await printLabels(label.svg, opts(), print, loadStatus, () => this.selectionValid() && this.dialog === dialog, this.abort?.signal); }
        finally { this.printing = false; if (this.dialog === dialog) render(); }
      });
      download.disabled = true;
      print.disabled = true;
      actions.append(download, print);
      controls.append(loadStatus, field("Label size", size), field("Print layout", paper), field("Copies for printing", count), warning, barcodeInfo, note, actions, node("p", "SVG downloads contain one label. Use Print to repeat labels or save a PDF with your browser.", { class: "ss-bin-label-footnote" }));
      const link = core.productURL(this.data.productUrl, root.location.href);
      if (link) controls.append(node("a", "View product details", { class: "ss-bin-label-link", href: link }));
      content.append(previewPanel, controls);
      dialog.append(heading, content);
      doc.body.append(dialog);
      dialog.showModal();
      this.abort = new AbortController();
      const lifetime = this.abort;
      const abort = new AbortController();
      const cancelAssets = () => abort.abort();
      lifetime.signal.addEventListener("abort", cancelAssets, { once: true });
      const timeout = root.setTimeout(() => abort.abort(), 12000);
      const assets = { logo: "", image: "" };
      const imageWarnings = [];
      const render = () => {
        label = core.renderLabel(this.data, opts(), assets);
        preview.replaceChildren();
        if (label.svg) preview.innerHTML = label.svg; // Renderer escapes all merchant data; image inputs are normalized PNG data URIs only.
        else preview.append(node("p", label.error || "Choose a label size."));
        barcodeInfo.textContent = !this.data.barcode && label.barcode?.bars ? "No product barcode is stored. This label encodes the actual SKU as a Code 128 barcode; it does not create a UPC or GTIN." : "";
        const warnings = [...imageWarnings, ...label.warnings];
        warning.textContent = warnings.join(" ");
        warning.hidden = !warnings.length;
        download.disabled = print.disabled = Boolean(loading || label.error || !label.svg || !this.selectionValid());
      };
      size.addEventListener("change", render);
      render();
      download.disabled = print.disabled = true;
      await Promise.all([
        (async () => {
          try { assets.logo = await rasterData(this.data.logo, abort.signal); }
          catch {
            try { assets.logo = await rasterData(this.data.fallbackLogo, abort.signal); }
            catch { imageWarnings.push("Logo could not be embedded; the label uses the Saber Sales & Service name."); }
          }
        })(),
        (async () => {
          if (!this.data.image) { imageWarnings.push("No product image is available for this variant."); return; }
          try { assets.image = await rasterData(this.data.image, abort.signal); }
          catch { imageWarnings.push("Product image could not be embedded. The label still includes the actual product identifiers."); }
        })(),
      ]);
      root.clearTimeout(timeout);
      lifetime.signal.removeEventListener("abort", cancelAssets);
      if (!this.isConnected || this.dialog !== dialog || !this.selectionValid()) return;
      loading = false;
      loadStatus.textContent = "Ready to download or print.";
      render();
    }
  }
  root.customElements.define("saber-bin-labels", BinLabels);
})(typeof window !== "undefined" ? window : null, function () {
  "use strict";
  const SIZES = Object.freeze({ small: Object.freeze({ widthMm: 101.6, heightMm: 50.8 }), large: Object.freeze({ widthMm: 152.4, heightMm: 76.2 }) });
  // ISO/IEC 15417 symbol-width table. Each value alternates bar/space widths; stop is 13 modules.
  const CODE128 = ("212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 112232 122132 122231 113222 123122 123221 223211 221132 221231 213212 223112 312131 311222 321122 321221 312212 322112 322211 212123 212321 232121 111323 131123 131321 112313 132113 132311 211313 231113 231311 112133 112331 132131 113123 113321 133121 313121 211331 231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 111242 121142 121241 114212 124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 114311 411113 411311 113141 114131 311141 411131 211412 211214 211232 2331112").split(" ");
  const EAN_L = ["0001101", "0011001", "0010011", "0111101", "0100011", "0110001", "0101111", "0111011", "0110111", "0001011"];
  const EAN_G = ["0100111", "0110011", "0011011", "0100001", "0011101", "0111001", "0000101", "0010001", "0001001", "0010111"];
  const EAN_PARITY = ["LLLLLL", "LLGLGG", "LLGGLG", "LLGGGL", "LGLLGG", "LGGLLG", "LGGGLL", "LGLGLG", "LGLGGL", "LGGLGL"];
  const plain = (s) => String(s ?? "").replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, "");
  const xml = (s) => plain(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" })[c]);
  function checkGTIN(value) {
    if (!/^\d{12,13}$/.test(value)) return false;
    const digits = value.split("").map(Number);
    const check = digits.pop();
    const sum = digits.reverse().reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0);
    return (10 - (sum % 10)) % 10 === check;
  }
  function bitsToBars(bits, quietLeft = 10, quietRight = 10) {
    const bars = [];
    let x = quietLeft;
    for (let i = 0; i < bits.length;) {
      const bit = bits[i];
      let j = i + 1;
      while (j < bits.length && bits[j] === bit) j++;
      if (bit === "1") bars.push({ x, width: j - i });
      x += j - i;
      i = j;
    }
    return { bars, modules: x + quietRight, quietLeft, quietRight, bits };
  }
  function encode128(value) {
    const c = /^\d{4,}$/.test(value) && value.length % 2 === 0;
    const codes = c ? [105, ...value.match(/../g).map(Number)] : [104, ...Array.from(value, (v) => v.charCodeAt(0) - 32)];
    const checksum = codes.reduce((sum, code, i) => sum + code * (i === 0 ? 1 : i), 0) % 103;
    codes.push(checksum, 106);
    const bits = codes.map((code) => Array.from(CODE128[code], (w, i) => (i % 2 === 0 ? "1" : "0").repeat(Number(w))).join("")).join("");
    return { ...bitsToBars(bits), codes, checksum, kind: c ? "Code 128 C" : "Code 128 B", minimumModuleMm: .19, barHeightMm: 18.28 };
  }
  function encodeEAN(value) {
    const full = value.length === 12 ? "0" + value : value;
    const parity = EAN_PARITY[Number(full[0])];
    const left = full.slice(1, 7).split("").map((n, i) => (parity[i] === "L" ? EAN_L : EAN_G)[Number(n)]).join("");
    const right = full.slice(7).split("").map((n) => EAN_L[Number(n)].replace(/[01]/g, (b) => b === "1" ? "0" : "1")).join("");
    return { ...bitsToBars("101" + left + "01010" + right + "101", value.length === 12 ? 9 : 11, value.length === 12 ? 9 : 7), kind: value.length === 12 ? "UPC-A" : "EAN-13", minimumModuleMm: .264, barHeightMm: 18.28 };
  }
  function encodeBarcode(value, options = {}) {
    const raw = String(value ?? "");
    const sku = String(options.sku ?? "");
    const text = raw || sku;
    const source = raw ? "Barcode" : "SKU barcode";
    if (!text) return { value: "", source, warning: "No barcode or SKU is stored for this variant. This is a product-name label only." };
    if (text.length > 512 || !/^[\x20-\x7e]+$/.test(text))
      return { value: text, source, warning: "The identifier is printed as text. Its characters are not supported by the available barcode formats." };
    let spec;
    if (raw && /^\d{12,13}$/.test(text)) {
      if (!checkGTIN(text)) return { value: text, source: "Stored barcode", warning: "The stored UPC/EAN check digit is invalid. It is shown as text; please have the product data checked." };
      spec = encodeEAN(text);
    } else spec = encode128(text);
    const available = Number(options.widthMm ?? 93.6);
    const moduleMm = spec.kind.startsWith("Code") ? Math.min(.381, available / spec.modules) : .264;
    if (!Number.isFinite(available) || moduleMm < spec.minimumModuleMm || spec.modules * moduleMm > available)
      return { value: text, source, warning: "This identifier is too long for a reliably scannable barcode at this label width. It remains readable text. Choose the wider 6 × 3 in label if needed." };
    return { ...spec, value: text, source: raw ? spec.kind : "SKU · " + spec.kind, moduleMm, widthMm: spec.modules * moduleMm, warning: "" };
  }
  function advance(value, mono = false) {
    return Array.from(value).reduce((sum, char) => sum + (mono ? 0.62 : /[W@%&]/.test(char) ? 1 : /[Mm]/.test(char) ? .9 : /[w]/.test(char) ? .8 : /[A-Z]/.test(char) ? .74 : /[ilI]/.test(char) ? .32 : /[fjt r.,:;!|]/.test(char) ? .4 : /[a-z0-9]/.test(char) ? .6 : char === " " ? .32 : 1), 0);
  }
  function measuredLines(value, width, size, mono = false) {
    const chars = Array.from(String(value));
    const lines = [];
    let line = "";
    for (const char of chars) {
      if (line && advance(line + char, mono) * size > width) { lines.push(line); line = ""; }
      line += char;
    }
    if (line) lines.push(line);
    return lines;
  }
  function normalizeData(data) {
    if (!data || typeof data !== "object" || !/^\d{1,20}$/.test(String(data.productId)) || !/^\d{1,20}$/.test(String(data.variantId))) throw new Error("Invalid product reference.");
    const fields = ["title", "variantTitle", "sku", "barcode", "image", "logo", "fallbackLogo", "website", "productUrl"];
    const clean = { productId: String(data.productId), variantId: String(data.variantId), selected: data.selected === true };
    for (const key of fields) {
      const value = String(data[key] ?? "");
      if (value.length > (key === "sku" || key === "barcode" ? 512 : 4096)) throw new Error("Label data is too long.");
      if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)) throw new Error("Label data contains unsupported control characters.");
      clean[key] = value;
    }
    if (!clean.title.trim()) throw new Error("Product title is unavailable.");
    return clean;
  }
  const png = (value) => /^data:image\/png;base64,[A-Za-z0-9+/]+=*$/.test(String(value || "")) ? value : "";
  function renderLabel(input, dimensions = SIZES.small, assets = {}) {
    const data = normalizeData(input);
    const widthMm = Number(dimensions.widthMm);
    const heightMm = Number(dimensions.heightMm);
    if (!Object.values(SIZES).some((s) => s.widthMm === widthMm && s.heightMm === heightMm)) throw new Error("Unsupported physical label dimensions.");
    const large = widthMm > 110;
    const warnings = [];
    const margin = large ? 4 : 3.6;
    const photoX = margin;
    const photoY = large ? 17 : 13;
    const photoWidth = large ? 36 : 23;
    const photoHeight = large ? 49 : 27.5;
    const contentX = large ? 45 : 30;
    const contentWidth = widthMm - contentX - margin;
    const skuFont = large ? 2.55 : 2.05;
    const referenceValue = data.sku || `Product ${data.productId} / Variant ${data.variantId}`;
    const referenceLines = measuredLines(referenceValue, contentWidth - 4, skuFont, true);
    const maximumReferenceLines = large ? 5 : 2;
    if (referenceLines.length > maximumReferenceLines) {
      return {
        svg: "", warnings: [large ? "The full identifier exceeds this label's readable reference area. Identifiers are never shortened." : "The full SKU does not fit this label size. Choose the wider 6 × 3 in label; identifiers are never shortened."],
        error: "Full identifier needs more space.", dimensions: { widthMm, heightMm },
      };
    }
    const barcode = encodeBarcode(data.barcode, { sku: data.sku, widthMm: contentWidth });
    if (barcode.warning) warnings.push(barcode.warning);
    let website = "www.sabersales.shop";
    try { const url = new URL(data.website); if (url.protocol === "https:" && !url.username && !url.password) website = url.host; } catch { /* Stable brand fallback. */ }
    const text = (x, y, value, attrs = "") => `<text x="${x}" y="${y}" ${attrs}>${xml(value)}</text>`;
    const fitted = (x, y, value, fontSize, width, attrs = "", mono = false) => text(x, y, value, `font-size="${fontSize}" textLength="${Math.min(width, advance(value, mono) * fontSize).toFixed(3)}" lengthAdjust="spacingAndGlyphs" ${attrs}`);
    const flowWords = (value, width, size) => {
      const words = plain(value).replace(/\s+/g, " ").trim().split(" ");
      const lines = [];
      let line = "";
      for (const word of words) {
        const combined = line ? line + " " + word : word;
        if (line && advance(combined) * size > width) { lines.push(line); line = ""; }
        const chunks = measuredLines(line ? line + " " + word : word, width, size);
        if (chunks.length > 1) { lines.push(...chunks.slice(0, -1)); line = chunks[chunks.length - 1]; }
        else line = chunks[0] || "";
      }
      if (line) lines.push(line);
      return lines;
    };
    const truncated = (value, width, size, mono = false) => {
      const chars = Array.from(value);
      while (chars.length && advance(chars.join("") + "…", mono) * size > width) chars.pop();
      return chars.join("") + "…";
    };
    const logo = png(assets.logo);
    const image = png(assets.image);
    const body = [];
    // A restrained brand rail and white header stay legible on ordinary label printers.
    body.push(`<path d="M0 .6H${widthMm}" stroke="#102b46" stroke-width="1.2"/><path d="M${widthMm - (large ? 22 : 15)} .6H${widthMm}" stroke="#0874ce" stroke-width="1.2"/>`);
    if (logo) body.push(`<image data-label-role="logo" x="${margin}" y="${large ? 3.2 : 2.7}" width="${large ? 38 : 29}" height="${large ? 9 : 7.4}" preserveAspectRatio="xMinYMid meet" href="${logo}"/>`);
    else body.push(fitted(margin, large ? 9.3 : 7.8, "SABER SALES & SERVICE", large ? 3.6 : 2.8, large ? 55 : 42, 'font-weight="700"'));
    body.push(fitted(widthMm - margin, large ? 9.4 : 7.8, website, large ? 3.1 : 2.5, widthMm - (large ? 67 : 49), 'data-label-role="website" text-anchor="end" font-weight="600"'));
    body.push(`<path d="M${margin} ${large ? 14.3 : 11.3}H${widthMm - margin}" stroke="#d2dfe9" stroke-width=".28"/>`);
    // The product photo occupies a useful, bounded column rather than floating in unused space.
    body.push(`<g data-label-role="photo-panel"><rect x="${photoX}" y="${photoY}" width="${photoWidth}" height="${photoHeight}" rx=".45" fill="#fff" stroke="#d5e0e8" stroke-width=".23"/>`);
    if (image) {
      body.push(`<image data-label-role="photo" x="${photoX + 1.2}" y="${photoY + 1.2}" width="${photoWidth - 2.4}" height="${photoHeight - 2.4}" preserveAspectRatio="xMidYMid meet" href="${image}"/>`);
    } else {
      body.push(`<path d="M${photoX + photoWidth * .27} ${photoY + photoHeight * .32}h${photoWidth * .46}v${photoHeight * .28}h-${photoWidth * .46}z" fill="none" stroke="#a7bac9" stroke-width=".5"/>`);
      body.push(fitted(photoX + photoWidth / 2, photoY + photoHeight * .76, "No photo", large ? 2.5 : 1.95, photoWidth - 3, 'text-anchor="middle" fill="#526578"'));
    }
    body.push("</g>");
    const variant = data.variantTitle && data.variantTitle !== "Default Title" ? data.variantTitle : "";
    if (variant) {
      const variantFont = large ? 2.6 : 2.1;
      const variantLines = flowWords(variant, photoWidth, variantFont);
      const visibleVariant = variantLines.slice(0, 2);
      if (variantLines.length > 2) visibleVariant[1] = truncated(visibleVariant[1], photoWidth, variantFont);
      body.push(text(photoX, large ? 68.4 : 43.0, "VARIANT", `data-label-role="variant-label" font-size="${large ? 1.8 : 1.5}" font-weight="700" fill="#526578" letter-spacing=".16"`));
      visibleVariant.forEach((line, i) => body.push(fitted(photoX, (large ? 71.4 : 45.6) + i * (large ? 3.4 : 2.65), line, variantFont, photoWidth, 'data-label-role="variant" font-weight="600"')));
    }
    const titleFont = large ? 4.0 : 2.7;
    const titleMaximum = large ? 3 : referenceLines.length === 1 ? 3 : 2;
    const allTitleLines = flowWords(data.title, contentWidth, titleFont);
    const visibleTitle = allTitleLines.slice(0, titleMaximum);
    if (allTitleLines.length > titleMaximum) visibleTitle[titleMaximum - 1] = truncated(visibleTitle[titleMaximum - 1], contentWidth, titleFont);
    visibleTitle.forEach((line, i) => body.push(fitted(contentX, (large ? 21.3 : 14.8) + i * (large ? 5.2 : 3.4), line, titleFont, contentWidth, 'data-label-role="title" font-weight="700"')));
    const referenceTop = large ? 34 : referenceLines.length === 1 ? 22.5 : 19.9;
    const referenceHeight = large ? Math.min(19.5, 7.7 + (referenceLines.length - 1) * 3.1) : referenceLines.length === 1 ? 5.2 : 7.8;
    body.push(`<g data-label-role="reference-panel"><rect x="${contentX}" y="${referenceTop}" width="${contentWidth}" height="${referenceHeight}" rx=".4" fill="#f1f5f8"/>`);
    body.push(text(contentX + 2, referenceTop + (large ? 2.8 : 1.9), data.sku ? "SKU / ITEM REFERENCE" : "PRODUCT REFERENCE", `font-size="${large ? 1.9 : 1.5}" font-weight="700" fill="#526578" letter-spacing=".12"`));
    referenceLines.forEach((line, i) => body.push(fitted(contentX + 2, referenceTop + (large ? 6.1 : referenceLines.length === 1 ? 4 : 4.1) + i * (large ? 3.1 : 2.6), line, skuFont, contentWidth - 4, `data-label-role="${data.sku ? "sku" : "product-reference"}" font-family="Courier New,monospace" font-weight="700" fill="#102b46"`, true)));
    body.push("</g>");
    // White barcode area is separate from photography, rules, tint and all product copy.
    const barTop = large ? Math.min(heightMm - 22.3, referenceTop + referenceHeight + 1.4) : heightMm - 22.3;
    if (barcode.bars) {
      barcode.barHeightMm = Math.max(barcode.barHeightMm, heightMm - 4.02 - barTop);
      if (large && (barcode.kind === "UPC-A" || barcode.kind === "EAN-13")) {
        // Grow both axes together inside the free region; keep the tested EAN/UPC proportions and quiet zones.
        const scale = Math.min(barcode.barHeightMm / 18.28, contentWidth / barcode.widthMm, 2.5);
        barcode.moduleMm *= scale;
        barcode.widthMm = barcode.modules * barcode.moduleMm;
      }
    }
    body.push(`<rect data-label-role="barcode-zone" x="${contentX}" y="${barTop - .4}" width="${contentWidth}" height="${heightMm - barTop + .4}" fill="#fff"/>`);
    if (barcode.bars) {
      const offset = contentX + (contentWidth - barcode.widthMm) / 2;
      const bars = barcode.bars.map((bar) => `<rect x="${bar.x}" y="0" width="${bar.width}" height="${barcode.barHeightMm}"/>`).join("");
      body.push(`<g data-label-role="barcode" fill="#000" transform="translate(${offset.toFixed(4)} ${barTop}) scale(${barcode.moduleMm.toFixed(6)} 1)">${bars}</g>`);
      body.push(fitted(contentX + contentWidth / 2, heightMm - 1.8, barcode.value, large ? 2.5 : 2.15, contentWidth, 'data-label-role="barcode-value" text-anchor="middle" font-family="Courier New,monospace" fill="#000"', true));
    } else if (data.barcode) {
      const barcodeFont = large ? 2.5 : 2.05;
      const lines = measuredLines(data.barcode, contentWidth, barcodeFont, true);
      if (lines.length > 5) return { svg: "", error: "Identifier needs a larger label.", warnings: ["The full identifier does not fit. It has not been shortened."], dimensions: { widthMm, heightMm } };
      body.push(text(contentX, barTop + 2.7, "STORED BARCODE · TEXT ONLY", `font-size="${large ? 1.9 : 1.5}" font-weight="700" fill="#526578"`));
      lines.forEach((line, i) => body.push(fitted(contentX, barTop + 6.1 + i * 2.8, line, barcodeFont, contentWidth, 'data-label-role="barcode-value" font-family="Courier New,monospace" fill="#172b3b"', true)));
      body.push(text(contentX, heightMm - 1.8, "No scannable barcode", `font-size="${large ? 2.1 : 1.8}" fill="#526578"`));
    } else {
      body.push(text(contentX, barTop + 7, "TEXT IDENTIFICATION", `font-size="${large ? 2.2 : 1.8}" font-weight="700" fill="#102b46"`));
      body.push(fitted(contentX, barTop + 11, "Use the reference above.", large ? 2.5 : 2.05, contentWidth, 'fill="#526578"'));
      body.push(text(contentX, heightMm - 1.8, "No scannable barcode", `font-size="${large ? 2.1 : 1.8}" fill="#526578"`));
    }
    const description = `${data.title}; ${variant}; SKU: ${data.sku || "not supplied"}; stored barcode: ${data.barcode || "not supplied"}; ${barcode.bars ? barcode.source : "text identifier only"}.`;
    // aria-label/desc preserve accessibility without a native SVG title tooltip covering the preview.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${widthMm}mm" height="${heightMm}mm" viewBox="0 0 ${widthMm} ${heightMm}" xml:space="preserve" role="img" aria-label="${xml(data.title)} bin label"><desc>${xml(description)}</desc><rect width="${widthMm}" height="${heightMm}" fill="#fff"/><g font-family="Arial,Helvetica,sans-serif" fill="#102b46">${body.join("")}</g></svg>`;
    return { svg, warnings, error: "", barcode, dimensions: { widthMm, heightMm } };
  }
  function printDocument(svg, options = {}) {
    const width = Number(options.widthMm), height = Number(options.heightMm), count = Number(options.count);
    if (!Object.values(SIZES).some((s) => s.widthMm === width && s.heightMm === height) || !Number.isInteger(count) || count < 1 || count > 50) throw new Error("Invalid print dimensions or copy count.");
    if (!/^<svg xmlns="http:\/\/www.w3.org\/2000\/svg"/.test(svg) || /<script|<foreignObject|\son[a-z]+\s*=\s*["']/i.test(svg)) throw new Error("Unsafe label document.");
    const paper = ["letter", "a4"].includes(options.paper) ? options.paper : "label";
    const sheetWidth = paper === "letter" ? 215.9 : 210;
    const sheetHeight = paper === "letter" ? 279.4 : 297;
    const cols = paper === "label" ? 1 : Math.max(1, Math.floor((sheetWidth - 10 + 2) / (width + 2)));
    const rows = paper === "label" ? 1 : Math.max(1, Math.floor((sheetHeight - 10 + 2) / (height + 2)));
    const perPage = cols * rows;
    let pages = "";
    for (let n = 0; n < count; n += perPage) pages += `<section class="sheet">${Array.from({ length: Math.min(perPage, count - n) }, () => `<div class="label">${svg}</div>`).join("")}</section>`;
    return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Saber bin labels</title><style>@page{size:${paper === "label" ? width + "mm " + height + "mm" : paper === "letter" ? "Letter" : "A4"};margin:0}*{box-sizing:border-box}html,body{margin:0;padding:0;background:#fff}.sheet{display:grid;grid-template-columns:repeat(${cols},${width}mm);grid-auto-rows:${height}mm;gap:${paper === "label" ? 0 : 2}mm;align-content:start;width:${paper === "label" ? width : sheetWidth}mm;height:${paper === "label" ? height : sheetHeight}mm;padding:${paper === "label" ? 0 : 5}mm;break-after:page;page-break-after:always}.sheet:last-child{break-after:auto;page-break-after:auto}.label{width:${width}mm;height:${height}mm;overflow:hidden;break-inside:avoid;page-break-inside:avoid}.label>svg{display:block;width:${width}mm;height:${height}mm}</style></head><body>${pages}</body></html>`;
  }
  function filename(data) {
    const id = String(data.sku || data.variantId).normalize("NFKD").replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || String(data.variantId);
    return "Saber-bin-label-" + id + ".svg";
  }
  function productURL(value, base) {
    try { const url = new URL(value, base); return url.origin === new URL(base).origin && /^https?:$/.test(url.protocol) && !url.username && !url.password ? url.href : ""; } catch { return ""; }
  }
  return Object.freeze({ SIZES, checkGTIN, encodeBarcode, normalizeData, renderLabel, printDocument, filename, productURL });
});
